import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const AI_VOICE_ASSISTANT_POSITIONS = {
  TOP_LEFT: "top-left",
  TOP_RIGHT: "top-right",
  BOTTOM_LEFT: "bottom-left",
  BOTTOM_RIGHT: "bottom-right",
};

const STORAGE_KEY = "sama-ai-voice-assistant-settings";

const DEFAULT_SETTINGS = {
  enabled: true,
  position: AI_VOICE_ASSISTANT_POSITIONS.BOTTOM_RIGHT,
};

const VALID_POSITIONS = new Set(Object.values(AI_VOICE_ASSISTANT_POSITIONS));

const normalizeSettings = (value) => {
  const source = value && typeof value === "object" ? value : {};
  const position = VALID_POSITIONS.has(source.position)
    ? source.position
    : DEFAULT_SETTINGS.position;

  return {
    enabled: typeof source.enabled === "boolean" ? source.enabled : DEFAULT_SETTINGS.enabled,
    position,
  };
};

const readStoredSettings = () => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? normalizeSettings(JSON.parse(raw)) : DEFAULT_SETTINGS;
  } catch (error) {
    console.error("Failed to read AI voice assistant settings:", error);
    return DEFAULT_SETTINGS;
  }
};

const AiVoiceAssistantContext = createContext({
  settings: DEFAULT_SETTINGS,
  setEnabled: () => {},
  setPosition: () => {},
  resetSettings: () => {},
});

export function AiVoiceAssistantProvider({ children }) {
  const [settings, setSettings] = useState(readStoredSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Failed to save AI voice assistant settings:", error);
    }
  }, [settings]);

  const setEnabled = useCallback((enabled) => {
    setSettings((prev) => ({
      ...prev,
      enabled: Boolean(enabled),
    }));
  }, []);

  const setPosition = useCallback((position) => {
    if (!VALID_POSITIONS.has(position)) return;

    setSettings((prev) => ({
      ...prev,
      position,
    }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const value = useMemo(
    () => ({
      settings,
      setEnabled,
      setPosition,
      resetSettings,
    }),
    [resetSettings, setEnabled, setPosition, settings]
  );

  return (
    <AiVoiceAssistantContext.Provider value={value}>
      {children}
    </AiVoiceAssistantContext.Provider>
  );
}

export const useAiVoiceAssistantSettings = () => useContext(AiVoiceAssistantContext);
